import { useAuth } from 'react-oidc-context';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) throw new Error('Missing VITE_API_URL');

class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function useToken(): string | undefined {
  const auth = useAuth();
  return auth.user?.id_token;
}

// ─── Domain types ─────────────────────────────────────────────────────────

export type Source = 'lcr' | 'steward';
export type HouseholdRole = 'head' | 'spouse' | 'child' | 'other';

export interface PersonSummary {
  id: string;
  household_id: string | null;
  name_full: string | null;
  name_first: string | null;
  name_last: string | null;
  sex: string | null;
  age: number | null;
  household_role: HouseholdRole | null;
  is_member: boolean | null;
  source: Source;
  status_override: PersonStatus | null;
}

export type PersonStatus = 'active' | 'semi_active' | 'inactive';

export interface Person extends PersonSummary {
  lcr_id: string | null;
  lcr_uuid: string | null;
  birth_date: string | null;
  email: string | null;
  phone: string | null;
  priesthood_office: string | null;
  is_prospective_elder: boolean | null;
  notes_override: string | null;
  // Free text — a title, an employer, or just the gist ("Burger King").
  occupation: string | null;
  tags: string[];
  status_flags: Record<string, boolean> | null;
  removed_at: string | null;
  lcr_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Household {
  id: string;
  source: Source;
  name_directory: string | null;
  address_lines: string[] | null;
  formatted_address: string | null;
  notes_override: string | null;
  tags: string[];
  // Resolved sector — override where one is set, otherwise the automatic
  // assignment. The _auto and _override values come alongside so the UI can
  // show what a person changed and offer to put it back.
  sector_macro: string | null;
  sector_micro: string | null;
  sector_macro_auto: string | null;
  sector_micro_auto: string | null;
  sector_macro_override: string | null;
  sector_micro_override: string | null;
  sector_unit: string | null;
  lat: number | null;
  lng: number | null;
  removed_at: string | null;
  lcr_synced_at: string | null;
  created_at: string;
  updated_at: string;
  members: PersonSummary[];
  interactions: Interaction[];
  links: HouseholdLink[];
}

export type HouseholdLinkKind = 'relatives' | 'same_address' | 'neighbors' | 'friends';

export interface HouseholdLink {
  other_id: string;
  kind: HouseholdLinkKind;
  note: string | null;
  name_directory: string | null;
  formatted_address: string | null;
}

export interface Calling {
  id: string;
  position: string;
  organization: string | null;
  /** 'ward' or 'stake' — stake callings are held by our members in the stake. */
  unit: 'ward' | 'stake';
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
}

export interface InteractionRef { id: string; name_full?: string; name_directory?: string }

export interface Interaction {
  id: string;
  occurred_at: string;
  type: string;
  notes: string | null;
  created_by: string;
  // Who actually had the interaction when it wasn't the recorder (free text).
  performed_by?: string | null;
  people?: InteractionRef[];
  households?: InteractionRef[];
  // On /people/{id}/interactions: how this interaction reached this person.
  // 'person' = directly linked; 'household' = linked to the person's household.
  linked_via?: 'person' | 'household';
}

export type InitiatorKind = 'lcr_sync' | 'user_ui' | 'user_api' | 'user_mcp' | 'system';
export type ResourceKind =
  | 'household'
  | 'person'
  | 'calling'
  | 'interaction'
  | 'companionship'
  | 'companionship_assignment';
export type ChangeAction = 'created' | 'updated' | 'removed' | 'restored';

export type MinisteringOrg = 'eq' | 'rs';

export interface MinisteringPersonRef {
  id: string;
  name_full: string | null;
  household_id?: string | null;
}

export interface AssignedHousehold {
  household_id: string;
  name_directory: string | null;
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
}

export interface AssignedSister {
  person_id: string;
  name_full: string | null;
  household_id: string | null;
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
}

export interface PersonAsMinister {
  companionship_id: string;
  organization: MinisteringOrg;
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
  co_ministers: MinisteringPersonRef[];
  assigned_households: AssignedHousehold[];
  assigned_sisters: AssignedSister[];
}

export interface PersonMinisteredTo {
  assignment_id: string;
  companionship_id: string;
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
  ministers: MinisteringPersonRef[];
}

export interface PersonMinistering {
  as_minister: PersonAsMinister[];
  as_ministered_to: PersonMinisteredTo[];
}

export interface HouseholdMinisteringAssignment {
  assignment_id: string;
  companionship_id: string;
  started_on: string;
  ended_on: string | null;
  is_active: boolean;
  ministers: MinisteringPersonRef[];
}

export interface HouseholdMinistering {
  assignments: HouseholdMinisteringAssignment[];
}

export interface ChangeLogEntry {
  id: number;
  occurred_at: string;
  initiator_kind: InitiatorKind;
  initiator_id: string | null;
  sync_run_id: number | null;
  resource_type: ResourceKind;
  resource_id: string;
  resource_name: string | null;
  action: ChangeAction;
  field: string | null;
  before_value: unknown;
  after_value: unknown;
  description: string | null;
}

export type RefKind = 'household' | 'person' | 'companionship';

export interface ChangeRef {
  kind: RefKind;
  name: string | null;
}

export type ChangeRefs = Record<string, ChangeRef>;

export interface Dashboard {
  last_sync: {
    source_file: string;
    started_at: string;
    finished_at: string;
    status: 'succeeded' | 'failed';
    households_upserted: number;
    people_upserted: number;
    households_removed: number;
    people_removed: number;
    callings_opened: number;
    callings_closed: number;
  } | null;
  counts: { households: number; people: number };
  trendline: {
    month: string;
    households_added: number;
    people_added: number;
    households_total: number;
    people_total: number;
  }[];
  recent_move_ins: { id: string; name_directory: string; formatted_address: string; created_at: string }[];
  recent_move_outs: { id: string; name_directory: string; formatted_address: string; removed_at: string }[];
  activity: {
    this_month: { total: number; by_type: Record<string, number> };
    households_contacted_90d: number;
    monthly: { month: string; interactions: number }[];
  };
  ministering: {
    assigned_households: number;
    ministered_90d: number;
    trend: { month: string; assigned: number; ministered: number }[];
    longest_uncontacted: {
      id: string;
      name_directory: string | null;
      formatted_address: string | null;
      last_contact_at: string | null;
    }[];
  };
  /**
   * Member households as a share of the homes inside the ward boundary.
   *
   * The denominator is addressable dwellings from UGRC's address points
   * (src/api/housing_units.json), not anything Steward maintains, so
   * `housing_units` is null until that census has been built. The numerator
   * is households the sector rules place inside the boundary — the same
   * ground on both sides of the ratio. `households_total` is every household
   * of record and is carried so the series can be re-cut later.
   */
  ward_coverage: {
    households_in_boundary: number;
    households_total: number;
    housing_units: number | null;
    share: number | null;
    census_generated: string | null;
    history: {
      as_of: string;
      households_in_boundary: number;
      households_total: number;
      housing_units: number | null;
      share: number | null;
    }[];
  } | null;
}

// ─── Hooks ────────────────────────────────────────────────────────────────

export interface SyncRun {
  id: number;
  source_file: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'succeeded' | 'failed';
  households_upserted: number;
  people_upserted: number;
  households_removed: number;
  people_removed: number;
  callings_opened: number;
  callings_closed: number;
  error_message: string | null;
}

export function useSyncs(params: { limit?: number; offset?: number } = {}) {
  const token = useToken();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  return useQuery({
    enabled: !!token,
    queryKey: ['syncs', params],
    queryFn: () =>
      request<{ syncs: SyncRun[]; limit: number; offset: number }>(
        `/syncs?${qs.toString()}`,
        token!,
      ),
  });
}

export function useDashboard() {
  const token = useToken();
  return useQuery({
    enabled: !!token,
    queryKey: ['dashboard'],
    queryFn: () => request<Dashboard>('/dashboard', token!),
    staleTime: 60_000,
  });
}

export function useSearch(
  target: 'people' | 'households',
  params: Record<string, string>,
  options?: { enabled?: boolean },
) {
  const token = useToken();
  const qs = new URLSearchParams({ type: target, ...params }).toString();
  return useQuery({
    enabled: !!token && (options?.enabled ?? true),
    queryKey: ['search', target, params],
    queryFn: () => request<{ type: string; results: any[] }>(`/search?${qs}`, token!),
    placeholderData: keepPreviousData,
  });
}

export function useHousehold(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['household', id],
    queryFn: () => request<Household>(`/households/${id}`, token!),
  });
}

/** The ward's defined sectors, for the override pickers. */
export interface SectorCatalog {
  macros: { name: string; micros: string[] }[];
}

export function useSectorCatalog() {
  const token = useToken();
  return useQuery({
    queryKey: ['sectors'],
    queryFn: () => request<SectorCatalog>('/sectors', token!),
    enabled: !!token,
    // Fixed in the rule table and only changes with a deploy.
    staleTime: Infinity,
  });
}

/**
 * Ward parcel geometry for the plat maps.
 *
 * Fetched through the API rather than as a static asset: the payload records
 * which parcels hold ward households, which is roster data even though the
 * county geometry under it is public. Cached hard — it only changes when
 * refresh_parcels is re-run.
 */
export function useParcels() {
  const token = useToken();
  return useQuery({
    queryKey: ['parcels'],
    queryFn: () => request<unknown>('/parcels', token!),
    enabled: !!token,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function usePatchHousehold() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Household> }) =>
      request<{ id: string }>(`/households/${id}`, token!, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['household', vars.id] });
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useAddHouseholdLink() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, other_id, kind, note }: { id: string; other_id: string; kind: HouseholdLinkKind; note?: string | null }) =>
      request<{ created: boolean }>(`/households/${id}/links`, token!, {
        method: 'POST',
        body: JSON.stringify({ other_id, kind, note: note ?? null }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['household', vars.id] });
      qc.invalidateQueries({ queryKey: ['household', vars.other_id] });
    },
  });
}

export function useUpdateHouseholdLink() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, other_id, kind, note }: { id: string; other_id: string; kind: HouseholdLinkKind; note: string | null }) =>
      request<{ updated: boolean }>(`/households/${id}/links`, token!, {
        method: 'PATCH',
        body: JSON.stringify({ other_id, kind, note }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['household', vars.id] });
      qc.invalidateQueries({ queryKey: ['household', vars.other_id] });
    },
  });
}

export function useDeleteHouseholdLink() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, other_id, kind }: { id: string; other_id: string; kind: HouseholdLinkKind }) => {
      const qs = new URLSearchParams({ other_id, kind }).toString();
      return request<{ deleted: boolean }>(`/households/${id}/links?${qs}`, token!, { method: 'DELETE' });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['household', vars.id] });
      qc.invalidateQueries({ queryKey: ['household', vars.other_id] });
    },
  });
}

export function usePerson(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['person', id],
    queryFn: () => request<Person>(`/people/${id}`, token!),
  });
}

export function usePatchPerson() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Person> }) =>
      request<{ id: string }>(`/people/${id}`, token!, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['person', vars.id] });
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function usePersonCallings(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['callings', id],
    queryFn: () => request<{ callings: Calling[] }>(`/people/${id}/callings`, token!),
  });
}

export function usePersonInteractions(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['person-interactions', id],
    queryFn: () =>
      request<{ interactions: Interaction[] }>(`/people/${id}/interactions`, token!),
  });
}

export function useInteractions(params: {
  q?: string;
  type?: string;
  occurred_after?: string;
  occurred_before?: string;
  limit?: number;
  offset?: number;
}) {
  const token = useToken();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return useQuery({
    enabled: !!token,
    queryKey: ['interactions', params],
    queryFn: () =>
      request<{ interactions: Interaction[]; limit: number; offset: number }>(
        `/interactions?${qs.toString()}`,
        token!,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useChanges(params: {
  resource_type?: ResourceKind;
  resource_id?: string;
  initiator_kind?: InitiatorKind;
  initiator_id?: string;
  sync_run_id?: number;
  action?: ChangeAction;
  after?: string;
  before?: string;
  limit?: number;
  offset?: number;
}) {
  const token = useToken();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return useQuery({
    enabled: !!token,
    queryKey: ['changes', params],
    queryFn: () =>
      request<{ changes: ChangeLogEntry[]; refs?: ChangeRefs; limit: number; offset: number }>(
        `/changes?${qs.toString()}`,
        token!,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useCreateInteraction() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      occurred_at: string;
      type: string;
      notes?: string;
      performed_by?: string;
      person_ids?: string[];
      household_ids?: string[];
    }) =>
      request<{ id: string }>(`/interactions`, token!, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions'] });
      qc.invalidateQueries({ queryKey: ['household'] });
      qc.invalidateQueries({ queryKey: ['person-interactions'] });
    },
  });
}

export function useUpdateInteraction() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      occurred_at?: string;
      type?: string;
      notes?: string;
      // Empty string clears the attribution back to the recorder.
      performed_by?: string;
    }) =>
      request<{ updated: boolean }>(`/interactions/${id}`, token!, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions'] });
      qc.invalidateQueries({ queryKey: ['household'] });
      qc.invalidateQueries({ queryKey: ['person-interactions'] });
    },
  });
}

export function usePersonMinistering(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['person-ministering', id],
    queryFn: () => request<PersonMinistering>(`/people/${id}/ministering`, token!),
  });
}

export function useHouseholdMinistering(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['household-ministering', id],
    queryFn: () => request<HouseholdMinistering>(`/households/${id}/ministering`, token!),
  });
}

// ─── Filter DSL types ─────────────────────────────────────────────────────

export type SearchScope = 'people' | 'households';
export type FilterValueType = 'string' | 'number' | 'date' | 'boolean' | 'enum';

export interface FieldSpec {
  value_type: FilterValueType;
  ops: string[];
  // Display name when the key reads poorly (status_override → "status").
  label?: string;
  // A bounded vocabulary: the value must be one of these.
  choices?: { value: string; label: string }[];
}

export interface SearchCatalog {
  scopes: SearchScope[];
  people: Record<string, FieldSpec>;
  households: Record<string, FieldSpec>;
  sort_fields: Record<SearchScope, string[]>;
}

export type SortDir = 'asc' | 'desc';

export interface SortField {
  field: string;
  dir: SortDir;
}

export interface FilterLeaf {
  field: string;
  op: string;
  value: unknown;
}

export interface FilterGroup {
  op: 'AND' | 'OR';
  conditions: FilterNode[];
}

export type FilterNode = FilterGroup | FilterLeaf;

export function isGroup(n: FilterNode): n is FilterGroup {
  return (n as FilterGroup).conditions !== undefined;
}

export interface SearchPeopleRow {
  id: string;
  household_id: string | null;
  name_full: string | null;
  sex: string | null;
  age: number | null;
  household_role: HouseholdRole | null;
  is_member: boolean | null;
  email: string | null;
  phone: string | null;
  source: Source;
  created_at: string;
  tags: string[];
  birth_date: string | null;
  status_override: PersonStatus | null;
  household_address: string | null;
  last_interaction_at: string | null;
  // Open callings, comma-joined; null when the person holds none.
  callings: string | null;
  occupation: string | null;
}

export interface SearchHouseholdRow {
  id: string;
  name_directory: string | null;
  formatted_address: string | null;
  source: Source;
  created_at: string;
  tags: string[];
  member_count: number;
  members: string | null;
  last_interaction_at: string | null;
  sector_macro: string | null;
  sector_micro: string | null;
  lat: number | null;
  lng: number | null;
  /**
   * The unit on its own — "APT 412". The address key the plat map joins on is
   * the street line and no longer carries it, so twelve doors of one building
   * share one key. Anything that lists households rather than mapping them
   * still has to tell them apart.
   */
  addr_unit: string | null;
  oldest_age: number | null;
  /**
   * The county lot this household sits on, resolved override-before-auto by
   * v_households. The plat map joins on this rather than deriving a key from
   * the address, so a corrected address reaches the map on the next sync
   * instead of at the next rebuild of parcels.json. Null when the matching
   * ladder could not place the household — those are the roster's
   * "no county parcel matches the address" rows.
   */
  parcel_key: string | null;
}

export interface SearchResults<T> {
  type: SearchScope;
  results: T[];
  limit: number;
  offset: number;
}

export interface ReportSummary {
  id: string;
  name: string;
  description: string | null;
  scope: SearchScope;
  sort_by: string | null;
  sort: SortField[] | null;
  columns: string[] | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_pinned: boolean;
  pinned_at: string | null;
}

export interface ReportFull extends Omit<ReportSummary, 'pinned_at'> {
  definition: FilterGroup;
}

// ─── Filter hooks ─────────────────────────────────────────────────────────

export function useSearchCatalog() {
  const token = useToken();
  return useQuery({
    enabled: !!token,
    queryKey: ['search-catalog'],
    queryFn: () => request<SearchCatalog>('/search/catalog', token!),
    staleTime: 5 * 60_000,
  });
}

export function useFilterSearch(
  scope: SearchScope,
  definition: FilterGroup,
  options?: { sort?: SortField[]; sort_by?: string; limit?: number; offset?: number; enabled?: boolean },
) {
  const token = useToken();
  const body = {
    scope,
    definition,
    sort: options?.sort,
    sort_by: options?.sort_by,
    limit: options?.limit ?? 500,
    offset: options?.offset ?? 0,
  };
  return useQuery({
    enabled: !!token && (options?.enabled ?? true),
    queryKey: ['filter-search', scope, body],
    queryFn: () =>
      request<SearchResults<SearchPeopleRow | SearchHouseholdRow>>(
        '/search',
        token!,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    placeholderData: keepPreviousData,
  });
}

// ─── Tags hooks ───────────────────────────────────────────────────────────

export interface TagSummary {
  name: string;
  created_at: string | null;
  household_count: number;
  people_count: number;
}

export function useTags() {
  const token = useToken();
  return useQuery({
    enabled: !!token,
    queryKey: ['tags'],
    queryFn: () => request<{ tags: TagSummary[] }>('/tags', token!),
    staleTime: 60_000,
  });
}

export function useCreateTag() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      request<{ name: string; created: boolean }>('/tags', token!, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });
}

// Rename/delete cascade into every household and person carrying the tag, so
// invalidate anything that displays tags.
function invalidateTagCarriers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['tags'] });
  qc.invalidateQueries({ queryKey: ['household'] });
  qc.invalidateQueries({ queryKey: ['person'] });
  qc.invalidateQueries({ queryKey: ['search'] });
  qc.invalidateQueries({ queryKey: ['filter-search'] });
  qc.invalidateQueries({ queryKey: ['changes'] });
}

export function useRenameTag() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, new_name }: { name: string; new_name: string }) =>
      request<{ renamed: boolean; name: string; merged: boolean }>('/tags', token!, {
        method: 'PATCH',
        body: JSON.stringify({ name, new_name }),
      }),
    onSuccess: () => invalidateTagCarriers(qc),
  });
}

export function useDeleteTag() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      request<{ deleted: boolean }>(`/tags?${new URLSearchParams({ name })}`, token!, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateTagCarriers(qc),
  });
}

// ─── Reports hooks ────────────────────────────────────────────────────────

export function useReports(params: { scope?: SearchScope } = {}) {
  const token = useToken();
  const qs = new URLSearchParams();
  if (params.scope) qs.set('scope', params.scope);
  const path = qs.toString() ? `/reports?${qs.toString()}` : '/reports';
  return useQuery({
    enabled: !!token,
    queryKey: ['reports', params],
    queryFn: () => request<{ reports: ReportSummary[] }>(path, token!),
  });
}

export function useReport(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['report', id],
    queryFn: () => request<ReportFull>(`/reports/${id}`, token!),
  });
}

export function useCreateReport() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string | null;
      scope: SearchScope;
      definition: FilterGroup;
      sort_by?: string | null;
      sort?: SortField[] | null;
      columns?: string[] | null;
    }) =>
      request<{ id: string }>('/reports', token!, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function usePatchReport() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ReportFull> }) =>
      request<{ id: string }>(`/reports/${id}`, token!, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['report', vars.id] });
    },
  });
}

export function useDeleteReport() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request<{ deleted: boolean }>(`/reports/${id}`, token!, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function usePinReport() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: boolean }) =>
      request<{ pinned: boolean }>(`/reports/${id}/pin`, token!, {
        method: pin ? 'POST' : 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useDeleteInteraction() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request<{ deleted: boolean }>(`/interactions/${id}`, token!, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions'] });
      qc.invalidateQueries({ queryKey: ['household'] });
      qc.invalidateQueries({ queryKey: ['person-interactions'] });
    },
  });
}

// ─── Incidents ────────────────────────────────────────────────────────────
// Emergency accountability. Mirrors src/api/incidents.py. Note the three
// orthogonal person axes — `accounted` (the sweep), `triage` (medical), and
// `needs` (unmet need) — which are deliberately not one fused enum: someone
// can be evacuated AND injured AND out of water.

export type IncidentStatus = 'planning' | 'active' | 'closed';
export type ContactResult = 'not_attempted' | 'answered' | 'no_answer' | 'vacant' | 'inaccessible';
export type Structure = 'unknown' | 'green' | 'yellow' | 'red';
export type SiteKind = 'residence' | 'command_post' | 'shelter' | 'staging' | 'aid_station' | 'other';
export type Accounted = 'unknown' | 'accounted' | 'not_home' | 'missing';
export type Triage = 'green' | 'yellow' | 'red' | 'black';
/** Derived server-side from a site's occupants — never stored. */
export type SiteAccountability =
  | 'no_occupants' | 'all_accounted' | 'partial' | 'none_accounted' | 'missing';

export interface Incident {
  id: string;
  name: string;
  kind: string | null;
  is_drill: boolean;
  status: IncidentStatus;
  current_round: number;
  activated_at: string | null;
  closed_at: string | null;
  seeded_at: string | null;
  notes: string | null;
  command_post_site_id: string | null;
  /** Set when government or stake ICS stands up and this becomes a resource
   *  reporting into their structure rather than its own top of the chart. */
  reporting_to: string | null;
  reporting_contact: string | null;
  /** Command and comms for this activation — printed on the packet's info
   *  page, and blank there until somebody fills them in. See migration 0026. */
  commander: string | null;
  commander_phone: string | null;
  deputy: string | null;
  deputy_phone: string | null;
  staging_area: string | null;
  radio_primary: string | null;
  radio_alt: string | null;
  frs_channel: string | null;
  net_schedule: string | null;
  comms_notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  site_count?: number;
  person_count?: number;
}

export interface IncidentContact {
  id: string;
  site_id: string | null;
  incident_person_id: string | null;
  name: string;
  relationship: string | null;
  phone: string | null;
  email: string | null;
  is_out_of_area: boolean;
  contact_attempted_at: string | null;
  contact_result: string | null;
  notes: string | null;
}

export interface IncidentPerson {
  id: string;
  site_id: string;
  person_id: string | null;
  name: string;
  age: number | null;
  sex: string | null;
  household_role: string | null;
  is_member: boolean | null;
  phone: string | null;
  accounted: Accounted;
  triage: Triage | null;
  needs: string[];
  disposition: string | null;
  found_at_site_id: string | null;
  medical_needs: string | null;
  special_needs: string[];
  notes: string | null;
  last_checked_at: string | null;
  arrived_at: string | null;
  departed_at: string | null;
  contacts: IncidentContact[];
}

export interface IncidentSite {
  id: string;
  household_id: string | null;
  label: string;
  address_lines: string[];
  formatted_address: string | null;
  lat: number | null;
  lng: number | null;
  /** {layer: value} — e.g. {"geo":"E Lorraine Dr","team":"Bravo"}. One value
   *  per layer; a site is in exactly one geographic sector. */
  sectors: Record<string, string>;
  contact_result: ContactResult;
  structure: Structure;
  hazards: string[];
  occupants_present: number | null;
  checked_round: number | null;
  last_checked_at: string | null;
  checked_by: string | null;
  notes: string | null;
  sort_order: number | null;
  /** Skipped: on the payload so it can be un-skipped, but out of all counts. */
  excluded: boolean;
  site_kind: SiteKind;
  /** Shelters only: how many the space actually holds. */
  capacity: number | null;
  /** The household is in the ward by its sector; null for a site with no
   *  household, where the pin is all there is to go on. */
  in_boundary: boolean | null;
  people: IncidentPerson[];
  contacts: IncidentContact[];
  accountability: SiteAccountability;
  swept_this_round: boolean;
}

export interface IncidentTask {
  id: string;
  site_id: string | null;
  body: string;
  priority: 'routine' | 'urgent' | 'life_safety';
  status: 'open' | 'in_progress' | 'done' | 'cancelled';
  assigned_to: string | null;
  opened_at: string;
  closed_at: string | null;
  closed_by: string | null;
  created_by: string;
}

export interface IncidentLogEntry {
  id: number;
  occurred_at: string;
  recorded_at: string;
  entry_type: 'status_change' | 'radio' | 'message' | 'note' | 'system';
  site_id: string | null;
  incident_person_id: string | null;
  body: string;
  created_by: string;
}

export interface BoardCounts {
  people_total: number;
  accounted: number;
  unknown: number;
  not_home: number;
  missing: number;
  triage_red: number;
  triage_yellow: number;
  triage_green: number;
  triage_black: number;
  with_needs: number;
  with_medical: number;
  sites_total: number;
  sites_swept: number;
  sites_unattempted: number;
  placard_red: number;
  placard_yellow: number;
  with_hazards: number;
  sites_off_map: number;
}

export interface SectorAssignment {
  layer: string;
  sector: string;
  assigned_to: string | null;
  phone: string | null;
  radio_channel: string | null;
  notes: string | null;
  started_at: string | null;
  last_contact_at: string | null;
}

/**
 * One thing that should be in the kit.
 *
 * The item belongs to the ward and the tick belongs to an activation, so
 * `checked` is only meaningful next to the incident it was fetched for. Asked
 * for without one — the standing list on the incidents page, with nothing
 * active — everything reads unchecked, which is the truth: no activation, no
 * ticks.
 */
export interface ChecklistItem {
  id: string;
  category: string;
  item: string;
  detail: string | null;
  checked: boolean;
  checked_at: string | null;
  checked_by: string | null;
  sort_order: number;
}

export interface Facility {
  id: string;
  label: string;
  site_kind: SiteKind;
  formatted_address: string | null;
  capacity: number | null;
  occupancy: number;
  adults: number;
  children: number;
  seniors: number;
  unknown_age: number;
  with_medical: number;
  people: IncidentPerson[];
}

export interface IncidentBoard {
  incident: Incident;
  sites: IncidentSite[];
  assignments: SectorAssignment[];
  facilities: Facility[];
  checklist: ChecklistItem[];
  open_tasks: IncidentTask[];
  log_tail: IncidentLogEntry[];
  counts: BoardCounts;
}

export interface SeedResult {
  sites_created: number;
  sites_skipped: number;
  people_created: number;
  site_total: number;
  person_total: number;
  carried_from?: string;
  people_enriched?: number;
  contacts_carried?: number;
}

export function useIncidents() {
  const token = useToken();
  return useQuery({
    enabled: !!token,
    queryKey: ['incidents'],
    queryFn: () => request<{ incidents: Incident[] }>('/incidents', token!),
  });
}

export function useIncidentBoard(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['incident-board', id],
    queryFn: () => request<IncidentBoard>(`/incidents/${id}/board`, token!),
    // The board is one bounded payload that the UI filters client-side, so it
    // is cheap to keep around and expensive to refetch mid-sweep.
    staleTime: 15_000,
  });
}

export function useIncidentSectors(id: string) {
  const token = useToken();
  return useQuery({
    enabled: !!token && !!id,
    queryKey: ['incident-sectors', id],
    queryFn: () =>
      request<{ layers: Record<string, { sector: string; site_count: number }[]> }>(
        `/incidents/${id}/sectors`, token!),
  });
}

export function useCreateIncident() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; kind?: string | null; is_drill?: boolean; notes?: string | null }) =>
      request<{ id: string }>('/incidents', token!, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

export function usePatchIncident() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Incident> }) =>
      request<{ id: string }>(`/incidents/${id}`, token!, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['incidents'] });
      qc.invalidateQueries({ queryKey: ['incident-board', vars.id] });
    },
  });
}

export function useSeedIncident() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, carry_forward_from }: { id: string; carry_forward_from?: string | null }) =>
      request<SeedResult>(`/incidents/${id}/seed`, token!, {
        method: 'POST',
        body: JSON.stringify({ carry_forward_from: carry_forward_from ?? null }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['incident-board', vars.id] });
      qc.invalidateQueries({ queryKey: ['incident-sectors', vars.id] });
      qc.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

/** Pull current roster names and addresses onto an existing incident.
 *  Touches only roster-derived fields — never status, sectors, or notes. */
export const useRefreshAddresses = () =>
  useBoardMutation<
    { incidentId: string; dry_run?: boolean },
    { sites: number; changed: number; samples: { before: string; after: string }[] }
  >((t, v) => request(`/incidents/${v.incidentId}/refresh-addresses`, t, {
      method: 'POST', body: JSON.stringify({ dry_run: v.dry_run ?? false }),
    }));

/** Start the next sweep pass. Everything recorded so far is preserved; sites
 *  simply fall behind the incident's round and read as needing another visit. */
export const useAdvanceRound = () =>
  useBoardMutation<{ incidentId: string }, { current_round: number; addresses_pending: number }>(
    (t, v) => request(`/incidents/${v.incidentId}/advance-round`, t, { method: 'POST' }),
  );

export function useDeleteIncident() {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request<{ deleted: boolean }>(`/incidents/${id}`, token!, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

// ─── Incident writes ──────────────────────────────────────────────────────
// Every mutation invalidates the single ['incident-board', id] key, because
// the board is one payload. `occurred_at` is optional on status writes so the
// operator can backdate a check a runner reported late.

function useBoardMutation<TVars extends { incidentId: string }, TOut>(
  fn: (token: string, vars: TVars) => Promise<TOut>,
) {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => fn(token!, vars),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['incident-board', vars.incidentId] });
      qc.invalidateQueries({ queryKey: ['incident-log', vars.incidentId] });
      qc.invalidateQueries({ queryKey: ['incident-tasks', vars.incidentId] });
    },
  });
}

export interface PersonPatch {
  accounted?: Accounted;
  triage?: Triage | null;
  needs?: string[];
  disposition?: string | null;
  found_at_site_id?: string | null;
  medical_needs?: string | null;
  special_needs?: string[];
  notes?: string | null;
  name?: string;
  age?: number | null;
  phone?: string | null;
  occurred_at?: string;
}

export interface SitePatch {
  label?: string;
  site_kind?: SiteKind;
  capacity?: number | null;
  formatted_address?: string | null;
  address_lines?: string[];
  lat?: number | null;
  lng?: number | null;
  contact_result?: ContactResult;
  structure?: Structure;
  hazards?: string[];
  occupants_present?: number | null;
  notes?: string | null;
  occurred_at?: string;
}

export const usePatchIncidentPerson = () =>
  useBoardMutation<{ incidentId: string; id: string; body: PersonPatch }, { changed: string[] }>(
    (t, v) => request(`/incident-people/${v.id}`, t, { method: 'PATCH', body: JSON.stringify(v.body) }),
  );

export const usePatchIncidentSite = () =>
  useBoardMutation<{ incidentId: string; id: string; body: SitePatch }, { changed: string[] }>(
    (t, v) => request(`/incident-sites/${v.id}`, t, { method: 'PATCH', body: JSON.stringify(v.body) }),
  );

/** The common sweep outcome in one call, rather than person by person. */
export const useSweepSite = () =>
  useBoardMutation<
    { incidentId: string; id: string; accounted?: Accounted; contact_result?: ContactResult; occurred_at?: string },
    { people_updated: number }
  >((t, v) =>
    request(`/incident-sites/${v.id}/sweep`, t, {
      method: 'POST',
      body: JSON.stringify({
        accounted: v.accounted ?? 'accounted',
        contact_result: v.contact_result ?? 'answered',
        occurred_at: v.occurred_at,
      }),
    }),
  );

export const useCreateIncidentSite = () =>
  useBoardMutation<
    { incidentId: string; label: string; formatted_address?: string | null;
      sectors?: Record<string, string>; lat?: number | null; lng?: number | null;
      site_kind?: SiteKind; capacity?: number | null },
    { id: string }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/sites`, t, {
      method: 'POST',
      body: JSON.stringify({
        label: v.label, formatted_address: v.formatted_address ?? null,
        sectors: v.sectors ?? {}, lat: v.lat ?? null, lng: v.lng ?? null,
        site_kind: v.site_kind ?? 'residence', capacity: v.capacity ?? null,
      }),
    }),
  );

export const useCreateIncidentPerson = () =>
  useBoardMutation<
    { incidentId: string; site_id: string; name: string; age?: number | null; phone?: string | null },
    { id: string }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/people`, t, {
      method: 'POST',
      body: JSON.stringify({ site_id: v.site_id, name: v.name, age: v.age ?? null, phone: v.phone ?? null }),
    }),
  );

/** Skip an address, or put it back. Not a delete: the row stays so the
 *  idempotent seed will not re-create it. */
export const useExcludeSite = () =>
  useBoardMutation<{ incidentId: string; id: string; excluded: boolean }, { excluded: boolean }>(
    (t, v) => request(`/incident-sites/${v.id}/exclude`, t, {
      method: 'POST', body: JSON.stringify({ excluded: v.excluded }),
    }),
  );

/** Move one site between sectors. An empty value clears that layer. */
export const useSetSiteSectors = () =>
  useBoardMutation<{ incidentId: string; id: string; sectors: Record<string, string> }, { sectors: Record<string, string> }>(
    (t, v) => request(`/incident-sites/${v.id}/sectors`, t, {
      method: 'PATCH', body: JSON.stringify({ sectors: v.sectors }),
    }),
  );

/** Fill a sector layer from a preset. Never overwrites hand-set values unless
 *  asked, so re-running cannot undo manual adjustment. */
export const useAutoSector = () =>
  useBoardMutation<
    { incidentId: string; layer?: string; strategy: 'ward' | 'street' | 'grid'; overwrite?: boolean },
    { layer: string; strategy: string; assigned: number; kept_existing: number; unplaceable: number }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/auto-sector`, t, {
      method: 'POST',
      body: JSON.stringify({ layer: v.layer ?? 'geo', strategy: v.strategy, overwrite: v.overwrite ?? false }),
    }),
  );

/** Name who is responsible for a sector. Upsert — clearing the name removes
 *  the assignment rather than leaving an empty shell. */
export const useAssignSector = () =>
  useBoardMutation<
    { incidentId: string; layer: string; sector: string; assigned_to: string;
      phone?: string | null; radio_channel?: string | null; notes?: string | null },
    { assigned_to: string | null }
  >((t, v) => request(`/incidents/${v.incidentId}/sector-assignment`, t, {
      method: 'POST',
      body: JSON.stringify({
        layer: v.layer, sector: v.sector, assigned_to: v.assigned_to,
        phone: v.phone ?? null, radio_channel: v.radio_channel ?? null, notes: v.notes ?? null,
      }),
    }));

export const useSectorCheckIn = () =>
  useBoardMutation<{ incidentId: string; layer: string; sector: string }, { last_contact_at: string }>(
    (t, v) => request(`/incidents/${v.incidentId}/sector-check-in`, t, {
      method: 'POST', body: JSON.stringify({ layer: v.layer, sector: v.sector }),
    }),
  );

/** Combine or split sectors. `from` merges whole sectors; `site_ids` moves a
 *  hand-picked set. An empty `to` clears the layer. */
export const useBulkSector = () =>
  useBoardMutation<
    { incidentId: string; layer: string; to: string; from?: string[]; site_ids?: string[] },
    { sites_changed: number }
  >((t, v) => request(`/incidents/${v.incidentId}/bulk-sector`, t, {
      method: 'POST',
      body: JSON.stringify({ layer: v.layer, to: v.to, from: v.from ?? [], site_ids: v.site_ids ?? [] }),
    }));

/** The standing go-kit, ticked for one incident when there is one to tick for. */
export function useGoKit(incidentId?: string) {
  const token = useToken();
  return useQuery({
    queryKey: ['go-kit', incidentId ?? null],
    queryFn: () => request<{ items: ChecklistItem[] }>(
      `/go-kit${incidentId ? `?incident=${incidentId}` : ''}`, token!),
    enabled: !!token,
  });
}

/**
 * Edit an item, or tick it off.
 *
 * Both go to the same row, because they are one thing to the person doing
 * them, but they land in different places: the wording is the ward's and the
 * tick is this incident's. Ticking without an incident is refused by the API
 * rather than guessed at.
 */
export const usePatchChecklistItem = () =>
  useGoKitMutation<{
    incidentId?: string; id: string;
    body: Partial<Pick<ChecklistItem, 'checked' | 'item' | 'detail' | 'category'>>;
  }>((t, v) => request(`/go-kit/${v.id}`, t, {
      method: 'PATCH',
      body: JSON.stringify({ ...v.body, incident_id: v.incidentId }),
    }));

export const useAddChecklistItem = () =>
  useGoKitMutation<{ incidentId?: string; item: string; category?: string; detail?: string | null }>(
    (t, v) => request('/go-kit', t, {
      method: 'POST',
      body: JSON.stringify({ item: v.item, category: v.category ?? 'Other', detail: v.detail ?? null }),
    }),
  );

export const useDeleteChecklistItem = () =>
  useGoKitMutation<{ incidentId?: string; id: string }>(
    (t, v) => request(`/go-kit/${v.id}`, t, { method: 'DELETE' }),
  );

/**
 * A go-kit write touches two things: the standing list, and whichever board
 * is showing it. Both are invalidated, because the same list is on screen in
 * two places and they must not disagree.
 */
function useGoKitMutation<V extends { incidentId?: string }>(
  fn: (token: string, vars: V) => Promise<unknown>,
) {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => fn(token!, vars) as Promise<{ id: string }>,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['go-kit'] });
      if (vars.incidentId) {
        qc.invalidateQueries({ queryKey: ['incident-board', vars.incidentId] });
      }
    },
  });
}

/** Check people in or out of a shelter. Checking in also accounts for them —
 *  one write, so there is never a shelter list disagreeing with a sweep list. */
export const useShelterCheckIn = () =>
  useBoardMutation<
    { incidentId: string; id: string; person_ids: string[]; departed?: boolean },
    { count: number }
  >((t, v) => request(`/incident-sites/${v.id}/shelter-check-in`, t, {
      method: 'POST',
      body: JSON.stringify({ person_ids: v.person_ids, departed: v.departed ?? false }),
    }));

export const useDeleteIncidentSite = () =>
  useBoardMutation<{ incidentId: string; id: string }, { deleted: boolean }>(
    (t, v) => request(`/incident-sites/${v.id}`, t, { method: 'DELETE' }),
  );

export const useDeleteIncidentPerson = () =>
  useBoardMutation<{ incidentId: string; id: string }, { deleted: boolean }>(
    (t, v) => request(`/incident-people/${v.id}`, t, { method: 'DELETE' }),
  );

export const useCreateIncidentContact = () =>
  useBoardMutation<
    { incidentId: string; site_id?: string | null; incident_person_id?: string | null;
      name: string; relationship?: string | null; phone?: string | null; is_out_of_area?: boolean },
    { id: string }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/contacts`, t, {
      method: 'POST',
      body: JSON.stringify({
        site_id: v.site_id ?? null, incident_person_id: v.incident_person_id ?? null,
        name: v.name, relationship: v.relationship ?? null, phone: v.phone ?? null,
        is_out_of_area: v.is_out_of_area ?? false,
      }),
    }),
  );

export const useDeleteIncidentContact = () =>
  useBoardMutation<{ incidentId: string; id: string }, { deleted: boolean }>(
    (t, v) => request(`/incident-contacts/${v.id}`, t, { method: 'DELETE' }),
  );

export const useCreateLogEntry = () =>
  useBoardMutation<
    { incidentId: string; body: string; entry_type?: IncidentLogEntry['entry_type'];
      site_id?: string | null; occurred_at?: string | null },
    { id: number }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/log`, t, {
      method: 'POST',
      body: JSON.stringify({
        body: v.body, entry_type: v.entry_type ?? 'note',
        site_id: v.site_id ?? null, occurred_at: v.occurred_at ?? null,
      }),
    }),
  );

export const useCreateTask = () =>
  useBoardMutation<
    { incidentId: string; body: string; priority?: IncidentTask['priority']; site_id?: string | null },
    { id: string }
  >((t, v) =>
    request(`/incidents/${v.incidentId}/tasks`, t, {
      method: 'POST',
      body: JSON.stringify({ body: v.body, priority: v.priority ?? 'routine', site_id: v.site_id ?? null }),
    }),
  );

export const usePatchTask = () =>
  useBoardMutation<
    { incidentId: string; id: string; body: Partial<Pick<IncidentTask, 'status' | 'priority' | 'assigned_to' | 'body'>> },
    { id: string }
  >((t, v) => request(`/incident-tasks/${v.id}`, t, { method: 'PATCH', body: JSON.stringify(v.body) }));
